/**
 * Segmented LCD knowledge-base articles.
 *
 * Single source of truth for the /learn hub and /learn/:slug article pages.
 * Plain CommonJS module.exports (not ESM `export`) so this file can be
 * `require()`-d directly by prerender.js (a plain Node script, not run
 * through webpack) as well as `import`-ed by React components -- webpack's
 * CJS/ESM interop handles the default-export mapping for the React side.
 *
 * Each entry powers: the clickable spec-strip pill on the Segmented LCD
 * page (SegmentedLCDPage.jsx SPECS array references these slugs), the
 * article page itself (src/pages/Learn/LearnArticlePage.jsx), the hub page
 * (src/pages/Learn/LearnHubPage.jsx), and per-route SEO metadata generated
 * into src/seo/pageMeta.js.
 */

const LEARN_ARTICLES = [
  // ── Technology ──────────────────────────────────────────────────────────
  // A single comparison page covers all four technologies rather than one
  // article per type -- all four spec-strip pills (TN, HTN, STN, FSTN) on
  // SegmentedLCDPage.jsx point at this one slug. Uses the `comparisonTable`
  // shape (see LearnArticlePage.jsx) instead of the standard
  // whatItIs/howItWorks/pros/cons/bestFor shape the other articles use.
  {
    slug: "tn-vs-htn-vs-stn-vs-fstn-lcd-comparison",
    category: "Technology",
    heading: "TN vs HTN vs STN vs FSTN: Segmented LCD Technology Compared",
    metaTitle: "TN vs HTN vs STN vs FSTN LCD Comparison | BIBA",
    metaDescription:
      "Compare TN, HTN, STN and FSTN segmented LCD technology -- how each works, contrast, viewing angle, cost and best use case. Supplied by BIBA Electronics.",
    summary:
      "TN, HTN, STN and FSTN compared side by side -- how each works, contrast, viewing angle, relative cost and best-fit application.",
    intro: [
      "TN, HTN, STN and FSTN are the four liquid-crystal technologies behind almost every segmented LCD in production today -- the kind of display used in calculators, digital clocks, panel meters, energy meters and multi-line industrial instrument panels. All four are built the same basic way, a liquid-crystal layer sandwiched between two polarizing filters, and all four are read out the same way, as fixed numeric, alphanumeric and icon segments rather than a pixel grid. What changes between them is the crystal's twist angle and pre-tilt, and that single variable is what controls viewing angle, contrast, and how many segments one controller can multiplex onto a small connector.",
      "Picking the right one comes down to three questions: how many digits and icons does the panel need, how will it be viewed (straight-on versus off-angle, lit versus unlit), and how tightly is cost constrained. The comparison table below lays out how TN, HTN, STN and FSTN answer those three questions differently, with a real sample of each technology BIBA has built.",
    ],
    supplierNote:
      "BIBA Electronics Pvt. Ltd. is a Gurgaon, India-based manufacturer and supplier of custom segmented LCD displays across all four of these technologies -- TN, HTN, STN and FSTN -- built to order for OEM customers worldwide. Every segmented LCD BIBA ships is engineered from a blank sheet around the customer's exact digit layout, icon set, glass size and PCB connector, in whichever of these four technologies best fits the application, rather than sold as a fixed catalog part. BIBA supplies segmented LCDs for instrumentation, appliances, automotive and industrial control applications from its manufacturing facility in Gurgaon.",
    comparisonTable: [
      {
        type: "TN",
        name: "Twisted Nematic",
        image: "/assets/images/lcd-type-tn.jpg",
        description:
          "The original, simplest segmented LCD technology: a 90° liquid-crystal twist with single-segment or static addressing.",
        contrast: "Moderate contrast, wide viewing angle",
        cost: "Lowest",
        bestFor: "Cost-sensitive counters & basic digital meters",
      },
      {
        type: "HTN",
        name: "High-Contrast TN",
        image: "/assets/images/lcd-type-htn.jpg",
        description:
          "A TN variant with a higher crystal pre-tilt angle, giving sharper on/off contrast than standard TN at a similar driving cost.",
        contrast: "High contrast, moderate-wide viewing angle",
        cost: "Low",
        bestFor: "Panel meters & handheld instruments needing crisp digits",
      },
      {
        type: "STN",
        name: "Super-Twisted Nematic",
        image: "/assets/images/lcd-type-stn.jpg",
        description:
          "A steeper 180-270° crystal twist gives a sharp threshold response, letting one controller multiplex far more segments than TN or HTN.",
        contrast: "Higher contrast at high multiplex ratios; natural blue/yellow tint",
        cost: "Moderate",
        bestFor: "Multi-digit, icon-heavy panels -- energy meters, data loggers",
      },
      {
        type: "FSTN",
        name: "Film-Compensated STN",
        image: "/assets/images/lcd-type-fstn.jpg",
        description:
          "STN with an added retardation film that cancels the natural tint, giving a true black-on-white image while keeping STN's segment density.",
        contrast: "High contrast, neutral black-on-white, backlight-friendly",
        cost: "Moderate-High",
        bestFor: "Backlit instrument clusters, automotive & outdoor equipment",
      },
    ],
    howToChoose:
      "As a rule of thumb: choose TN for the simplest, lowest-cost display with only a handful of digits viewed mostly straight-on. Choose HTN when those same few digits need to read more sharply at a glance, without taking on STN's multiplexing complexity. Choose STN when the panel needs many digits or icons multiplexed off a small connector -- the standard choice for energy meters and data loggers. Choose FSTN when you need STN's segment density but also want a clean, neutral, backlight-friendly appearance rather than STN's natural tint. BIBA's team can also recommend the right technology directly once you share your digit count, icon list and viewing environment.",
  },

  // ── Mode ─────────────────────────────────────────────────────────────────
  {
    slug: "reflective-lcd-mode",
    category: "Mode",
    pill: "Reflective",
    heading: "Reflective Mode",
    metaTitle: "Reflective LCD Mode: How It Works & When to Use It | BIBA",
    metaDescription:
      "Reflective LCDs use ambient light instead of a backlight. How the mode works, its pros and cons, and where it fits vs transmissive/transflective.",
    summary:
      "Uses ambient light bounced back through the display -- no backlight needed, and the best readability in direct sunlight.",
    whatItIs:
      "A reflective LCD has a mirror-like reflector behind the liquid-crystal layer instead of a backlight. It works by reflecting whatever ambient light is available back through the display to the viewer's eye.",
    howItWorks:
      "Light from the room or the sun passes through the front polarizer and liquid-crystal layer, hits the rear reflector, and bounces back out through the same layers. Segments that are switched 'on' block or scatter that returning light so they appear dark against a bright reflective background -- with no backlight consuming power at all.",
    pros: [
      "Lowest power consumption of any LCD mode -- ideal for battery and coin-cell devices",
      "Best readability of any mode in bright ambient light or direct sunlight",
      "Simplest, lowest-cost construction -- no backlight components",
    ],
    cons: [
      "Unreadable in the dark or dim environments without external light",
      "Contrast and brightness depend entirely on ambient lighting conditions",
    ],
    bestFor:
      "Battery-powered, outdoor or bright-environment products where power budget matters more than low-light readability -- remote controls, outdoor meters, basic instruments.",
  },
  {
    slug: "transmissive-lcd-mode",
    category: "Mode",
    pill: "Transmissive",
    heading: "Transmissive Mode",
    metaTitle: "Transmissive LCD Mode Explained | BIBA",
    metaDescription:
      "Transmissive LCDs rely entirely on a backlight for visibility. How the mode works, its trade-offs, and when it's the right choice.",
    summary:
      "Relies entirely on a backlight shining through the display -- unreadable without one, but consistent in any lighting and in total darkness.",
    whatItIs:
      "A transmissive LCD has no reflector behind the liquid-crystal layer -- instead, a backlight (LED or EL) sits behind it and shines through, and the display is only visible because of that backlight.",
    howItWorks:
      "Light from the backlight passes through the rear polarizer, the liquid-crystal layer, and the front polarizer to reach the viewer. Segments that are switched 'on' block that light path, appearing dark against the backlit background; without the backlight powered, the display is effectively invisible.",
    pros: [
      "Fully readable in complete darkness -- performance doesn't depend on ambient light",
      "Consistent appearance and contrast regardless of lighting conditions",
      "Works well for backlit brand colors and night-use products",
    ],
    cons: [
      "Highest power draw of the three modes -- backlight must always run for the display to be visible",
      "Washes out and becomes hard to read in very bright ambient/sunlight conditions",
      "Requires backlight hardware, adding cost and cell thickness",
    ],
    bestFor:
      "Products used in low light or darkness where the display must always be visible regardless of ambient conditions -- dashboards, indoor appliances, night-use instruments.",
  },
  {
    slug: "transflective-lcd-mode",
    category: "Mode",
    pill: "Transflective",
    heading: "Transflective Mode",
    metaTitle: "Transflective LCD Mode: Best of Both Worlds | BIBA",
    metaDescription:
      "Transflective LCDs combine a reflector and a backlight for readability in both bright and dark conditions. How it works and when to specify it.",
    summary:
      "A partially reflective layer lets the display use ambient light by day and a backlight by night -- readable in almost any condition.",
    whatItIs:
      "A transflective LCD replaces the fully mirrored reflector of a reflective display with a partially reflective, partially transparent layer, and adds a backlight behind it -- combining both modes in one panel.",
    howItWorks:
      "In bright ambient light, the partial reflector bounces enough incoming light back through the display for the segments to read clearly, just like a reflective panel -- so the backlight can stay off to save power. In low light, the backlight shines through the same partially transparent layer, keeping the display visible in the dark. The panel works well across the full range between those two extremes.",
    pros: [
      "Readable across the widest range of lighting conditions, from bright sun to darkness",
      "Backlight can be switched off in bright conditions to save power",
      "One design covers use cases that would otherwise need two different display modes",
    ],
    cons: [
      "Slightly lower peak contrast than a dedicated reflective or transmissive panel in its best-case lighting",
      "More complex construction than either mode alone, with a modest cost premium",
    ],
    bestFor:
      "Portable and outdoor-indoor products that move between lighting conditions -- handheld instruments, automotive and off-highway dashboards, field equipment.",
  },

  // ── Voltage ──────────────────────────────────────────────────────────────
  {
    slug: "3v-lcd-drive-voltage",
    category: "Voltage",
    pill: "3V",
    heading: "3V Drive Voltage",
    metaTitle: "3V LCD Drive Voltage: When to Choose It | BIBA",
    metaDescription:
      "3V segmented LCDs pair naturally with coin-cell and low-voltage battery systems. How 3V driving works and its trade-offs vs 5V.",
    summary:
      "Matches coin-cell and low-voltage battery rails directly -- the natural choice for compact, battery-powered devices.",
    whatItIs:
      "A 3V-rated segmented LCD is designed to be driven directly from a 3V supply -- typically a CR2032 or similar coin cell, or two AA/AAA cells -- without a voltage-boost circuit in between.",
    howItWorks:
      "The LCD's contrast and switching threshold are set during manufacturing to match a 3V RMS drive waveform. Driving it directly from a matching 3V rail keeps the driving circuit as simple as possible -- often just a microcontroller's own LCD driver peripheral or a small dedicated LCD driver IC -- with no need for a separate voltage regulator dedicated to the display.",
    pros: [
      "Drives directly from common 3V coin-cell and battery packs -- no boost converter needed",
      "Simplest, lowest-component-count driving circuit",
      "Lower power draw than boosting to a higher drive voltage",
    ],
    cons: [
      "Contrast can be marginally lower than an equivalent 5V panel in some constructions",
      "Less headroom if the supply voltage sags as a battery discharges",
    ],
    bestFor:
      "Coin-cell and low-voltage battery products -- remote controls, small handheld instruments, wearables -- where keeping the driving circuit simple matters.",
  },
  {
    slug: "5v-lcd-drive-voltage",
    category: "Voltage",
    pill: "5V",
    heading: "5V Drive Voltage",
    metaTitle: "5V LCD Drive Voltage: When to Choose It | BIBA",
    metaDescription:
      "5V segmented LCDs suit systems already running a 5V rail or microcontroller. How 5V driving compares to 3V and custom voltages.",
    summary:
      "Matches a common 5V logic rail directly -- the usual choice for mains-powered or 5V-microcontroller-based products.",
    whatItIs:
      "A 5V-rated segmented LCD is designed to be driven from a 5V supply -- the same rail many microcontrollers, industrial instruments and mains-powered devices already run on.",
    howItWorks:
      "As with 3V panels, the LCD's electro-optic threshold is set during manufacturing to match a 5V RMS drive waveform. If the rest of the product's electronics already run at 5V, this lets the display share that rail directly rather than needing a separate voltage domain just for the LCD.",
    pros: [
      "Shares a rail already present in most 5V microcontroller and mains-powered designs",
      "Typically offers a slightly wider contrast/temperature margin than 3V panels",
      "No extra voltage regulator needed if the rest of the board is already 5V",
    ],
    cons: [
      "Not a direct match for coin-cell or 3V battery systems without a boost converter",
      "Marginally higher power draw than an equivalent 3V panel",
    ],
    bestFor:
      "Panel meters, industrial instruments and mains- or 5V-rail-powered products where the display can share the board's existing 5V supply.",
  },
  {
    slug: "custom-lcd-drive-voltage",
    category: "Voltage",
    pill: "Custom",
    heading: "Custom Drive Voltage",
    metaTitle: "Custom LCD Drive Voltage for Non-Standard Systems | BIBA",
    metaDescription:
      "When 3V or 5V doesn't match your system, a segmented LCD can be engineered to a custom drive voltage. How and when to specify it.",
    summary:
      "Engineered to match whatever supply rail your product actually runs on, instead of forcing a voltage converter onto the design.",
    whatItIs:
      "Beyond the common 3V and 5V options, a segmented LCD's electro-optic threshold can be engineered to match almost any drive voltage a product's electronics already use -- common alternatives include 1.5V single-cell systems, 9V, 12V and 24V industrial rails, or intermediate values in between.",
    howItWorks:
      "The liquid-crystal mixture, cell gap and polarizer selection are tuned during manufacturing so the display's optimal contrast lands at the target voltage, the same way it's tuned for 3V or 5V -- there's nothing electrically special about those two values, they're just the most common options kept as standard.",
    pros: [
      "Removes the need for a dedicated voltage regulator or booster just for the display",
      "Matches unusual or legacy system voltages without redesigning the rest of the board",
      "One less compromise when integrating a display into an existing product",
    ],
    cons: [
      "Requires a custom engineering pass rather than pulling from a standard voltage option",
      "Longer lead time than a stock 3V or 5V design",
    ],
    bestFor:
      "Products built around an existing non-3V/5V supply rail -- industrial equipment on 12V/24V, legacy instrument platforms, or single-cell 1.5V devices -- where adapting the display is simpler than adapting the whole system.",
  },

  // ── Temperature ──────────────────────────────────────────────────────────
  {
    slug: "industrial-temperature-range-lcd",
    category: "Temperature",
    pill: "-20°C to +70°C (Industrial)",
    heading: "Industrial Temperature Range (-20°C to +70°C)",
    metaTitle: "-20°C to +70°C: Industrial LCD Temperature Range | BIBA",
    metaDescription:
      "Why segmented LCDs need an industrial -20°C to +70°C rating, what happens outside it, and how BIBA engineers for extended ranges.",
    summary:
      "The standard industrial rating BIBA builds to as a baseline -- and what actually happens to an LCD outside that window.",
    whatItIs:
      "-20°C to +70°C is the industrial-grade operating temperature range BIBA's segmented LCDs are built to as a baseline -- wide enough to cover outdoor equipment, vehicle dashboards and unconditioned industrial enclosures, well beyond a typical indoor-only consumer rating.",
    howItWorks:
      "Liquid crystal is a fluid, and its viscosity and molecular response change with temperature. Below the rated minimum, the crystal thickens and segments respond sluggishly or fail to switch cleanly; above the rated maximum, the crystal can lose its ordered structure entirely and the display loses contrast or blanks out until it cools. The rated range is the window over which the display keeps its full contrast and response speed. BIBA can also engineer beyond -20°C to +70°C -- down to -40°C or up to +85°C -- for applications that need it, at the cost of some trade-off in room-temperature contrast.",
    pros: [
      "Covers the overwhelming majority of outdoor, automotive and industrial use cases out of the box",
      "No added cost over a standard consumer-grade display",
      "Can be extended further (e.g. -40°C to +85°C) when the application calls for it",
    ],
    cons: [
      "Extended ranges beyond -20°C to +70°C typically trade off some room-temperature contrast",
      "Very cold or very hot extremes still slow response time even within the rated window",
    ],
    bestFor:
      "Any product that won't live in a climate-controlled indoor space -- outdoor meters, vehicle and off-highway dashboards, industrial control panels.",
  },

  // ── Backlight ────────────────────────────────────────────────────────────
  {
    slug: "no-backlight-reflective-lcd",
    category: "Backlight",
    pill: "None",
    heading: "No Backlight (Reflective-Only)",
    metaTitle: "No-Backlight (Reflective-Only) LCDs | BIBA",
    metaDescription:
      "Segmented LCDs with no backlight rely purely on ambient light. When skipping the backlight makes sense, and its trade-offs.",
    summary:
      "The lowest-power, lowest-cost backlight option: none at all, relying entirely on ambient light like a reflective-mode display.",
    whatItIs:
      "A 'None' backlight option means the segmented LCD has no illumination source behind it at all -- it's built purely as a reflective (or transflective) panel that relies entirely on ambient light, the same way a basic calculator or wristwatch display works.",
    howItWorks:
      "Without a backlight layer, the display simply reflects whatever ambient light is available, the same mechanism as reflective mode. There's no LED array or EL panel to drive, no extra current draw, and no added cell thickness from a backlight assembly.",
    pros: [
      "Lowest possible power draw -- nothing to power beyond the LCD driver itself",
      "Lowest cost -- no backlight hardware or driving circuitry",
      "Thinnest cell construction",
    ],
    cons: [
      "Unreadable in the dark without an external light source",
      "Not an option if the product needs to be used in low-light conditions",
    ],
    bestFor:
      "Battery-critical or cost-critical products used mainly in daylight or well-lit conditions -- basic remotes, calculators, simple meters.",
  },
  {
    slug: "led-backlight-lcd",
    category: "Backlight",
    pill: "LED",
    heading: "LED Backlight",
    metaTitle: "LED Backlight for Segmented LCDs | BIBA",
    metaDescription:
      "LED backlighting is the standard choice for illuminated segmented LCDs. How it works, its pros and cons versus EL backlighting.",
    summary:
      "The standard illumination choice for most backlit segmented LCDs -- bright, efficient, and available in almost any color.",
    whatItIs:
      "An LED backlight places one or more LEDs (or an LED-lit light guide) behind the LCD panel to illuminate it from behind, making the display readable in low light or complete darkness.",
    howItWorks:
      "LEDs mounted at the edge or behind the panel shine through a diffuser or light guide that spreads the light evenly across the display area, then through the LCD layers to the viewer. Color is set by the LED itself -- white, amber, green, blue and other colors are all straightforward options -- and brightness can be adjusted by controlling LED current.",
    pros: [
      "High brightness and long operating lifetime",
      "Wide choice of colors, including white and multi-color options",
      "Efficient -- good brightness per milliamp of current draw",
      "Simple to dim or switch via standard LED driving circuitry",
    ],
    cons: [
      "Higher power draw than no backlight or EL in most configurations",
      "Adds some thickness and cost to the cell assembly",
      "Uneven illumination is possible with poor diffuser/light-guide design (BIBA engineers around this)",
    ],
    bestFor:
      "Most backlit products -- dashboards, indoor appliances, industrial panels -- where brightness, color choice and lifetime matter more than the absolute lowest power draw.",
  },
  {
    slug: "el-backlight-lcd",
    category: "Backlight",
    pill: "EL",
    heading: "EL (Electroluminescent) Backlight",
    metaTitle: "EL (Electroluminescent) Backlight for LCDs | BIBA",
    metaDescription:
      "Electroluminescent (EL) backlighting gives a segmented LCD ultra-thin, uniform illumination. How it compares to LED backlighting.",
    summary:
      "A thin electroluminescent panel that glows uniformly across its whole area -- an alternative to LED for slim, evenly lit displays.",
    whatItIs:
      "An EL (electroluminescent) backlight is a thin phosphor-coated panel that glows when an alternating current is applied across it, placed behind the LCD to illuminate it evenly across its entire surface.",
    howItWorks:
      "The EL panel's phosphor layer emits light directly when excited by an AC voltage, rather than relying on point-source LEDs and a diffuser to spread light evenly. Because the entire panel glows at once, illumination is inherently uniform with no visible hot spots, and the panel itself is extremely thin.",
    pros: [
      "Very uniform, hot-spot-free illumination across the whole display area",
      "Extremely thin profile -- thinner than most LED backlight assemblies",
      "Distinctive soft glow, often used for a premium appearance",
    ],
    cons: [
      "Requires an AC driver circuit rather than simple DC LED driving",
      "Brightness typically lower than LED, and phosphor brightness fades gradually over its lifetime",
      "Fewer color options than LED",
    ],
    bestFor:
      "Slim-profile products where uniform, even illumination and a thin cell stack matter more than maximum brightness -- premium instrument panels and compact devices.",
  },

  // ── Interface ────────────────────────────────────────────────────────────
  {
    slug: "static-drive-lcd",
    category: "Interface",
    pill: "Static",
    heading: "Static Drive",
    metaTitle: "Static Drive LCDs: Simplest Segment Control | BIBA",
    metaDescription:
      "Static drive gives every LCD segment its own connection for the simplest, highest-contrast control. When it's the right choice.",
    summary:
      "Every segment gets its own dedicated connection -- the simplest, highest-contrast way to drive an LCD, at the cost of more pins.",
    whatItIs:
      "Static drive (also called direct drive) gives every individual segment of the LCD its own dedicated electrical connection back to the driver, with no sharing of drive lines between segments.",
    howItWorks:
      "Because each segment has a direct, independent connection, the driver can switch each one on or off without any interaction or interference from other segments -- there's no multiplexing logic, no shared row/column addressing, and no risk of 'ghosting' between segments that a shared drive line can introduce.",
    pros: [
      "Simplest possible driving scheme -- straightforward to design and debug",
      "Best possible contrast, since there's no multiplex-related crosstalk",
      "No multiplex driver IC required for very simple displays",
    ],
    cons: [
      "Needs one pin/connection per segment, so pin count grows fast with segment count",
      "Becomes impractical for panels with more than a handful of segments",
    ],
    bestFor:
      "Simple, low-segment-count displays -- a handful of digits or icons -- where minimizing complexity and maximizing contrast matters more than segment density.",
  },
  {
    slug: "multiplex-drive-lcd",
    category: "Interface",
    pill: "Multiplex (1/2, 1/3, 1/4)",
    heading: "Multiplex Drive (1/2, 1/3, 1/4)",
    metaTitle: "Multiplex Drive LCDs: 1/2, 1/3, 1/4 Explained | BIBA",
    metaDescription:
      "Multiplex driving lets one controller address many more LCD segments than static drive. How multiplex ratios like 1/3 and 1/4 work.",
    summary:
      "Segments share rows and columns of drive lines, letting far more of them be controlled from far fewer connections.",
    whatItIs:
      "Multiplex drive organizes segments into a grid of shared row and column drive lines -- described by a ratio like 1/2, 1/3 or 1/4 -- so a controller can address many more segments than it has physical pins for.",
    howItWorks:
      "Instead of one dedicated connection per segment, groups of segments share common lines, and the driver cycles through rows fast enough that the human eye perceives them as being on continuously (the same principle as scanning a matrix keypad or LED display). A 1/3 multiplex ratio, for example, drives the panel in three time-sliced passes per refresh cycle, letting one set of drive lines control roughly three times as many segments as a static (1/1) scheme could with the same pin count. Higher ratios (1/4 and beyond) pack in still more segments, at the cost of needing a display technology -- typically STN or FSTN -- with a steep enough electro-optic response to keep contrast clean at that ratio.",
    pros: [
      "Dramatically fewer connections needed for a given segment count",
      "Scales to the high segment/icon counts complex panels need",
      "Standard, well-supported driving scheme with widely available driver ICs",
    ],
    cons: [
      "Needs a display technology (STN/FSTN) suited to the chosen multiplex ratio to avoid contrast loss",
      "Slightly more complex driving circuitry and timing than static drive",
    ],
    bestFor:
      "Multi-digit, icon-dense panels -- the same energy meters, data loggers and instrument clusters that call for STN or FSTN technology -- where pin count has to stay manageable.",
  },

  // ── Compliance ───────────────────────────────────────────────────────────
  {
    slug: "rohs-compliance-lcd",
    category: "Compliance",
    pill: "RoHS Certified",
    heading: "RoHS Compliance",
    metaTitle: "RoHS Certified Segmented LCDs: What It Means | BIBA",
    metaDescription:
      "What RoHS certification covers for a segmented LCD, why it matters for OEM buyers, and how BIBA ensures every display complies.",
    summary:
      "Restriction of Hazardous Substances compliance -- a baseline every BIBA segmented LCD meets, and a requirement for selling into most global markets.",
    whatItIs:
      "RoHS (Restriction of Hazardous Substances) is an EU directive -- now a de facto global standard -- that restricts the use of lead, mercury, cadmium and several other hazardous substances in electronic and electrical products, including displays.",
    howItWorks:
      "Compliance covers everything that goes into the finished display: the glass, the liquid-crystal material, the polarizers, the conductive adhesives and the connector/PCB interface, all sourced and processed to stay under RoHS's substance thresholds. BIBA builds every segmented LCD to RoHS-compliant materials and processes as standard, and can supply compliance documentation for customer audits or end-product certification.",
    pros: [
      "Required to legally sell most electronic products in the EU and increasingly worldwide",
      "Signals a baseline of manufacturing and material quality control",
      "No cost premium -- it's how BIBA builds every display already",
    ],
    cons: [],
    bestFor:
      "Every product shipping into the EU or any market that recognizes RoHS -- which today is most of the world's OEM electronics market.",
  },

  // ── Customization ────────────────────────────────────────────────────────
  {
    slug: "custom-segmented-lcd-design",
    category: "Customization",
    pill: "100% — every project is custom-built",
    heading: "100% Custom LCD Design",
    metaTitle: "Why Every BIBA Segmented LCD Is 100% Custom-Built | BIBA",
    metaDescription:
      "There's no off-the-shelf segmented LCD part number -- every project is engineered from a blank sheet. What that process looks like.",
    summary:
      "There is no catalog of stock segmented LCDs to choose from -- every layout, icon set and connector is drawn specifically for your product.",
    whatItIs:
      "Unlike a TFT module or a standard component you can pick off a datasheet, a segmented LCD's glass is drawn specifically for the product it goes into -- the exact digit layout, icon shapes, glass outline and connector position are all part of the design, not a configuration option on a pre-existing part.",
    howItWorks:
      "The process starts from the application: what needs to be shown (digits, icons, symbols), how it needs to fit the enclosure, and how it connects to the PCB. From there, BIBA draws the segment layout in-house to match that exact requirement, builds a prototype sample for validation against the real enclosure and electronics, and only moves to mass production once that sample is approved.",
    pros: [
      "Display fits the product exactly -- no-compromise digit layout or icon set",
      "Any icon, symbol or brand-specific glyph can be included, not just standard characters",
      "Glass size and connector position are drawn to match your PCB, not the other way around",
    ],
    cons: [
      "Longer lead time than pulling a part off a shelf, since a layout has to be drawn and prototyped first",
      "Requires sharing enclosure/PCB details up front to get an accurate design",
    ],
    bestFor:
      "Any product where the display needs to fit a specific enclosure, show specific icons, or integrate with an existing PCB -- which in practice is nearly every segmented LCD BIBA builds.",
  },
];

module.exports = LEARN_ARTICLES;
